<script setup lang="ts" generic="T">
import type { TBaseTreeNode, TBaseTreeSelectionMode } from "@/components/ui/tree/base-tree.types";

import { cloneDeep, isEqual } from "es-toolkit";
import { computed, h, ref, useTemplateRef, watch } from "vue";

import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { useTypedIntl } from "@/locales/typed-intl";
import { chain } from "@/utils/chain";

import Chip from "primevue/chip";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputText from "primevue/inputtext";
import { type ConditionCallback, VirtualisedTree } from "vue-virtualised";

import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";
import IndeterminatedCheckbox from "@/components/ui/checkbox/indeterminated-checkbox.vue";

interface IProps {
  selectionMode?: TBaseTreeSelectionMode;
  filter?: (query: string, node: TBaseTreeNode<T>) => boolean;
  nodes: Array<TBaseTreeNode<T>>;
  nodeHeight?: number;
  parentsOnly?: boolean; // if true, only parents are selected, all children (deep) of selected parents are disabled
  searchLabel?: string;
  disabled?: boolean;
  enableSelectAll?: boolean;
  useChips?: boolean;
}

interface ISlot {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  default?: (node: TBaseTreeNode<T>) => any;
  selectAllLabel: () => unknown;
}

type TSelectionPayloadType<K extends IProps["selectionMode"]> = K extends "multiple"
  ? Array<TBaseTreeNode<T>>
  : TBaseTreeNode<T>;

interface IEmit {
  (e: "selection", payload: TSelectionPayloadType<typeof props.selectionMode>): void;
}

const slots = defineSlots<ISlot>();
const emits = defineEmits<IEmit>();

const { t, k } = useTypedIntl();

const props = withDefaults(defineProps<IProps>(), {
  selectionMode: "none",
  nodeHeight: 45,
  searchLabel: undefined,
  filter: undefined,
});

const treeView = useTemplateRef<typeof VirtualisedTree>("treeView");
const realNodes = ref<Array<TBaseTreeNode<T>>>([]);
const selectedNodes = ref<Array<TBaseTreeNode<T>>>([]);
const disabledNodes = ref<Record<TBaseTreeNode<T>["id"], TBaseTreeNode<T>>>({});
const searchQuery = ref<string>("");
const shouldEmit = ref<boolean>(true);
const isAllNodesSelected = ref<boolean>(false);

const filteredNodes = computed(() => {
  if (!props.filter) {
    return realNodes.value;
  }

  if (!searchQuery.value.length) {
    return realNodes.value;
  }

  function buildNode(node: TBaseTreeNode<T>): TBaseTreeNode<T> | null {
    const isValid = props.filter!(searchQuery.value, node);

    if (isValid) {
      return node;
    }

    if (!node.children) {
      return null;
    }

    const filteredChildren = chain(node.children).filterMap(buildNode).value();

    if (!filteredChildren.length) {
      return null;
    }

    return {
      ...node,
      children: filteredChildren,
    };
  }

  return chain(realNodes.value)
    .filterMap((n) => {
      // there is currently no other solution for the generic type T & UnwrapRef<T> than casting
      return buildNode(n as TBaseTreeNode<T>);
    })
    .value();
});

function handleClickNode(node: TBaseTreeNode<T>): void {
  if (!isNodeSelectable(node)) {
    return;
  }

  toggleSelectedNode(node);
}

function select(cb: (node: TBaseTreeNode<T>) => boolean) {
  shouldEmit.value = false;

  selectedNodes.value = [];
  realNodes.value.forEach((node) => deepToggleDisableNodeChildren(node as TBaseTreeNode<T>, false));

  const flattenned = realNodes.value.flatMap((n) => flattenNode(n as TBaseTreeNode<T>));
  for (const node of flattenned) {
    if (cb(node)) {
      handleClickNode(node);
    }
  }

  shouldEmit.value = true;
}

function selectAllNodes(): void {
  if (props.selectionMode === "multiple" && props.parentsOnly) {
    selectedNodes.value = cloneDeep(realNodes.value);
    // disable all node children with parents only true
    selectedNodes.value.forEach((n) => unselectAndDisableAllChildren(n as TBaseTreeNode<T>));
  }
}

function unselectNode(node: TBaseTreeNode<T>): void {
  const selectedIndex = selectedNodes.value.findIndex((sn) => sn.id === node.id);
  if (selectedIndex === -1) {
    return;
  }
  selectedNodes.value.splice(selectedIndex, 1);
}

function unselectAndDisableAllChildren(node: TBaseTreeNode<T>): void {
  if (!(props.selectionMode === "multiple")) return;

  if (props.parentsOnly) {
    // unselect all children and disable them
    deepToggleSelectNodeChildren(node, false);
    deepToggleDisableNodeChildren(node, true);
  } else {
    deepToggleSelectNodeChildren(node, true);
  }
}

function unselectAllNodes(): void {
  if (props.selectionMode === "multiple" && props.parentsOnly) {
    // enable all node children before clearing selected nodes in multiple mode && parents only
    selectedNodes.value.forEach((n) => deepToggleDisableNodeChildren(n as TBaseTreeNode<T>, false));
  }
  selectedNodes.value = [];
}

function toggleSelectedNode(node: TBaseTreeNode<T>): void {
  if (props.selectionMode === "none") {
    return;
  }

  if (props.selectionMode === "multiple") {
    const maybeAlreadySelectedNode = selectedNodes.value.find((sn) => sn.id === node.id);

    if (maybeAlreadySelectedNode) {
      unselectNode(maybeAlreadySelectedNode as TBaseTreeNode<T>);
      deepToggleSelectNodeChildren(node, false);

      if (props.parentsOnly) {
        // enable all children
        deepToggleDisableNodeChildren(node, false);
      }
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    selectedNodes.value.push(node as any);
    unselectAndDisableAllChildren(node);
    return;
  }

  // unselect node if mode is single select and node was already selected
  if (selectedNodes.value[0]?.id === node.id) {
    unselectAllNodes();
    return;
  }

  selectedNodes.value = [node];
}

/**
 * This function is used solely in multiple selection mode, and for now with parentsOnly set to true
 */
function deepToggleSelectNodeChildren(node: TBaseTreeNode<T>, select: boolean): void {
  // if there is a filter query, the selection should be applied to the real nodes and not the filtered ones
  if (searchQuery.value.length) {
    const maybeRealNode = findNode(realNodes.value as Array<TBaseTreeNode<T>>, node as TBaseTreeNode<T>);
    if (!maybeRealNode) return;

    maybeRealNode.children?.forEach((child: TBaseTreeNode<T>) => {
      const maybeAlreadySelectedNode = selectedNodes.value.find((sn) => sn.id === child.id);

      if (select && !maybeAlreadySelectedNode) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        selectedNodes.value.push(child as any);
      } else if (!select && maybeAlreadySelectedNode) {
        unselectNode(maybeAlreadySelectedNode as TBaseTreeNode<T>);
      }

      deepToggleSelectNodeChildren(child, select);
    });

    return;
  }
  if (!node.children?.length) return;

  node.children.forEach((child: TBaseTreeNode<T>) => {
    const maybeAlreadySelectedNode = selectedNodes.value.find((sn) => sn.id === child.id);

    if (select && !maybeAlreadySelectedNode) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      selectedNodes.value.push(child as any);
    } else if (!select && maybeAlreadySelectedNode) {
      unselectNode(maybeAlreadySelectedNode as TBaseTreeNode<T>);
    }

    deepToggleSelectNodeChildren(child, select);
  });
}

/**
 * This function is used solely in multiple selection mode, and for now with parentsOnly true
 */
function deepToggleDisableNodeChildren(node: TBaseTreeNode<T>, disabled: boolean): void {
  // if there is a filter query, the disabling should be applied to the real nodes and not the filtered ones
  if (searchQuery.value.length) {
    const maybeRealNode = findNode(realNodes.value as Array<TBaseTreeNode<T>>, node);
    if (!maybeRealNode) return;

    maybeRealNode.children?.forEach((child: TBaseTreeNode<T>) => {
      const maybeDisabledNode = disabledNodes.value[child.id];

      if (!maybeDisabledNode && disabled) {
        disabledNodes.value[child.id] = child;
      } else if (!disabled) {
        delete disabledNodes.value[child.id];
      }

      if (child.children?.length) {
        deepToggleDisableNodeChildren(child, disabled);
      }
    });
    return;
  }

  if (!node.children?.length) return;

  node.children.forEach((child: TBaseTreeNode<T>) => {
    const maybeDisabledNode = disabledNodes.value[child.id];

    if (!maybeDisabledNode && disabled) {
      disabledNodes.value[child.id] = child;
    } else if (!disabled) {
      delete disabledNodes.value[child.id];
    }

    if (child.children?.length) {
      deepToggleDisableNodeChildren(child, disabled);
    }
  });
}

function findNode(treeNodes: Array<TBaseTreeNode<T>>, node: TBaseTreeNode<T>): TBaseTreeNode<T> | undefined {
  let maybeResult = treeNodes.find((n) => {
    return n.id === node.id;
  });

  if (!maybeResult) {
    for (let i = 0; i < treeNodes.length; i++) {
      if (treeNodes[i].children?.length) {
        maybeResult = findNode(treeNodes[i].children!, node);
        if (maybeResult) {
          break;
        }
      }
    }
  }

  return maybeResult;
}

function isNodeSelected(node: TBaseTreeNode<T>): boolean {
  return selectedNodes.value.find((sn) => sn.id === node.id) !== undefined;
}

function isNodeDisabled(node: TBaseTreeNode<T>): boolean {
  return disabledNodes.value[node.id] !== undefined;
}

function isNodeSelectable(node: TBaseTreeNode<T>): boolean {
  if (node.state?.disabled) {
    return false;
  }

  if (props.selectionMode === "none") {
    return false;
  }

  if (props.selectionMode === "multiple" && props.parentsOnly && isNodeDisabled(node)) {
    return false;
  }

  return true;
}

function isNodesArrayEqual(array1: TBaseTreeNode<T>[], array2: TBaseTreeNode<T>[]): boolean {
  const array1SortedIds = chain(array1)
    .map((n) => n.id)
    .sort()
    .value();
  const array2SortedIds = chain(array2)
    .map((n) => n.id)
    .sort()
    .value();

  return isEqual(array1SortedIds, array2SortedIds);
}

async function toggleCollapseNode(node: TBaseTreeNode<T>, index: number) {
  const expanded = node.state ? !node.state.expanded : true;

  function updateNode(root: TBaseTreeNode<T>): TBaseTreeNode<T> {
    if (root.id === node.id) {
      return {
        ...root,
        state: {
          ...root.state,
          expanded,
        },
        children: root.children?.map(updateNode) ?? undefined,
      };
    }

    return {
      ...root,
      children: root.children?.map(updateNode) ?? undefined,
    };
  }

  realNodes.value = realNodes.value.map((r) => updateNode(r as TBaseTreeNode<T>));

  if (treeView.value) {
    await treeView.value.updateNode(filteredNodes.value, node, index, () => ({
      ...node,
      state: {
        ...node.state,
        expanded,
      },
    }));
  }
}

function toggleCollapseAll(collapsed: boolean) {
  if (!treeView.value) {
    return;
  }

  function updateNode(node: TBaseTreeNode<T>): TBaseTreeNode<T> {
    return {
      ...node,
      state: {
        ...node.state,
        expanded: collapsed,
      },
      children: node.children ? node.children.map(updateNode) : node.children,
    };
  }

  for (let i = 0; i < realNodes.value.length; i++) {
    const node = realNodes.value[i];
    treeView.value.updateNode(filteredNodes.value, node, i, updateNode);
  }

  treeView.value.forceUpdate();
}

function collapseAll() {
  toggleCollapseAll(false);
}

function uncollapseAll() {
  toggleCollapseAll(true);
}

function scrollToNode(conditionalCallback: ConditionCallback) {
  treeView.value?.scrollToNode(conditionalCallback);
}

function treeCellRenderer(node: TBaseTreeNode<T>, index: number) {
  return [
    h(
      "div",
      {
        class: "tree-node",
        role: "treeitem",
        "aria-expanded": node.state?.expanded ? "true" : "false",
        "aria-setsize": node.children?.length ?? 0,
        "aria-level": node.parents.length + 1,
        "data-pc-section": "node",
        "data-test-id": DATA_TEST_IDS.components.baseTree.tree.node,
        "data-node-id": node.id,
      },
      [
        ...node.parents.map(() =>
          h("div", {
            class: "ml-8 w-0 h-full border-gray-200 border-l !rounded-none",
          }),
        ),
        h(
          "div",
          {
            class: [
              "tree-node-content",
              "w-full",
              isNodeSelectable(node) ? "tree-node-selectable" : "tree-node-disabled",
              isNodeSelected(node) ? "highlight" : undefined,
            ],
            "data-pc-section": "content",
            "data-highlight": isNodeSelected(node) ? "true" : "false",
            "data-selectable": isNodeSelectable(node) ? "true" : "false",
            role: isNodeSelectable(node) ? "button" : undefined,
            onClick: () => handleClickNode(node),
          },
          [
            h(
              "button",
              {
                type: "button",
                class: "tree-toggler",
                tabindex: "-1",
                "aria-hidden": (node.children?.length ?? 0) > 0 ? "true" : "false",
                "data-pc-section": "toggler",
                "data-pd-ripple": "true",
                onClick: async (ev: Event) => {
                  ev.stopPropagation();
                  ev.preventDefault();

                  await toggleCollapseNode(node, index);
                },
              },
              [
                (node.children?.length ?? 0) > 0
                  ? h(
                      "svg",
                      {
                        width: 14,
                        height: 14,
                        viewbox: "0 0 14 14",
                        fill: "none",
                        xmlns: "http://www.w3c.org/2000/svg",
                        class: "tree-toggler-icon",
                      },
                      [
                        node.state?.expanded
                          ? h("path", {
                              fill: "currentColor",

                              d: "M7.01744 10.398C6.91269 10.3985 6.8089 10.378 6.71215 10.3379C6.61541 10.2977 6.52766 10.2386 6.45405 10.1641L1.13907 4.84913C1.03306 4.69404 0.985221 4.5065 1.00399 4.31958C1.02276 4.13266 1.10693 3.95838 1.24166 3.82747C1.37639 3.69655 1.55301 3.61742 1.74039 3.60402C1.92777 3.59062 2.11386 3.64382 2.26584 3.75424L7.01744 8.47394L11.769 3.75424C11.9189 3.65709 12.097 3.61306 12.2748 3.62921C12.4527 3.64535 12.6199 3.72073 12.7498 3.84328C12.8797 3.96582 12.9647 4.12842 12.9912 4.30502C13.0177 4.48162 12.9841 4.662 12.8958 4.81724L7.58083 10.1322C7.50996 10.2125 7.42344 10.2775 7.32656 10.3232C7.22968 10.3689 7.12449 10.3944 7.01744 10.398Z",
                            })
                          : h("path", {
                              fill: "currentColor",

                              d: "M4.38708 13C4.28408 13.0005 4.18203 12.9804 4.08691 12.9409C3.99178 12.9014 3.9055 12.8433 3.83313 12.7701C3.68634 12.6231 3.60388 12.4238 3.60388 12.2161C3.60388 12.0084 3.68634 11.8091 3.83313 11.6622L8.50507 6.99022L3.83313 2.31827C3.69467 2.16968 3.61928 1.97313 3.62287 1.77005C3.62645 1.56698 3.70872 1.37322 3.85234 1.22959C3.99596 1.08597 4.18972 1.00371 4.3928 1.00012C4.59588 0.996539 4.79242 1.07192 4.94102 1.21039L10.1669 6.43628C10.3137 6.58325 10.3962 6.78249 10.3962 6.99022C10.3962 7.19795 10.3137 7.39718 10.1669 7.54416L4.94102 12.7701C4.86865 12.8433 4.78237 12.9014 4.68724 12.9409C4.59212 12.9804 4.49007 13.0005 4.38708 13Z",
                            }),
                      ],
                    )
                  : undefined,
              ],
            ),
            h(
              "span",
              {
                class: "p-tree-node-label w-full",
                "data-pc-section": "label",
              },
              [
                h(
                  "div",
                  {
                    class: "flex content-center items-center",
                  },
                  [slots.default ? slots.default(node) : node.name],
                ),
              ],
            ),
          ],
        ),
      ],
    ),
  ];
}

function flattenNode(root?: TBaseTreeNode<T> | null, result: Array<TBaseTreeNode<T>> = []): Array<TBaseTreeNode<T>> {
  if (!root) {
    return result;
  }

  result.push(root);
  if (root.children) {
    root.children.forEach((child: TBaseTreeNode<T>) => flattenNode(child, result));
  }

  return result;
}

watch(
  () => props.nodes,
  () => {
    realNodes.value = props.nodes;
  },
  { immediate: true },
);

watch(
  selectedNodes,
  () => {
    if (!shouldEmit.value) {
      return;
    }

    if (props.selectionMode === "single") {
      emits("selection", selectedNodes.value[0] as TBaseTreeNode<T>);
    } else if (props.selectionMode === "multiple") {
      emits("selection", selectedNodes.value as TBaseTreeNode<T>[]);

      if (props.parentsOnly) {
        isAllNodesSelected.value = isNodesArrayEqual(selectedNodes.value as TBaseTreeNode<T>[], props.nodes)
          ? true
          : false;
      }
    }
  },
  { deep: true },
);

watch(
  filteredNodes,
  () => {
    treeView.value?.forceUpdate();
  },
  { immediate: true },
);

watch(isAllNodesSelected, (newValue) => {
  if (newValue) {
    selectAllNodes();
  } else if (isNodesArrayEqual(selectedNodes.value as TBaseTreeNode<T>[], realNodes.value as TBaseTreeNode<T>[])) {
    // if all nodes were selected, reset selectedNodes
    unselectAllNodes();
  }
});

defineExpose({
  scrollToNode,
  collapseAll,
  uncollapseAll,
  select,
});
</script>

<template>
  <div
    class="base-tree flex flex-col gap-2 relative"
    :data-test-id="DATA_TEST_IDS.components.baseTree.component"
  >
    <div
      v-if="props.disabled"
      class="absolute top-0 bottom-0 left-0 right-0 bg-white opacity-40 z-50"
    />

    <label
      v-if="props.searchLabel"
      :data-test-id="DATA_TEST_IDS.components.baseTree.searchInput.label"
    >
      {{ props.searchLabel }}
    </label>

    <IconField>
      <InputIcon class="pi pi-search" />
      <InputText
        v-model="searchQuery"
        :data-test-id="DATA_TEST_IDS.components.baseTree.searchInput.input"
        :placeholder="t(k.global.misc.search)"
      />
    </IconField>

    <div
      v-if="props.useChips && selectedNodes.length"
      class="flex flex-col gap-2"
      :data-test-id="DATA_TEST_IDS.components.baseTree.chips.component"
    >
      <div class="flex w-full justify-end">
        <HospitalityButton
          :data-test-id="DATA_TEST_IDS.components.baseTree.chips.clearAllButton"
          :label="t(k.global.misc.clearAll)"
          severity="link"
          @click="unselectAllNodes"
        />
      </div>
      <div class="border rounded-md p-2 gap-2 max-h-[10rem] overflow-auto">
        <Chip
          v-for="node in selectedNodes"
          :key="node.id"
          :data-node-id="node.id"
          :data-test-id="DATA_TEST_IDS.components.baseTree.chips.selectedNodeChip"
          :label="node.name"
          :pt="{
            root: {
              class: '!py-1 !px-2 m-1',
            },
            label: {
              class: '!text-sm',
            },
            removeIcon: {
              class: '!text-text-secondary !text-xs',
            },
          }"
          removable
          remove-icon="pi pi-times"
          @remove="toggleSelectedNode(node as TBaseTreeNode<T>)"
        />
      </div>
    </div>

    <div
      v-if="props.selectionMode === 'multiple' && props.enableSelectAll"
      class="flex w-full items-center py-2"
    >
      <label
        class="cursor-pointer flex flex-1"
        :data-test-id="DATA_TEST_IDS.components.baseTree.selectAll.label"
        for="select-all"
      >
        <slot name="selectAllLabel">
          {{ t(k.global.misc.selectAll) }}
        </slot>
      </label>

      <IndeterminatedCheckbox
        v-model="isAllNodesSelected"
        binary
        :data-test-id="DATA_TEST_IDS.components.baseTree.selectAll.checkbox"
        input-id="select-all"
      />
    </div>

    <div
      class="border rounded-md"
      :data-test-id="DATA_TEST_IDS.components.baseTree.tree.container"
    >
      <VirtualisedTree
        ref="treeView"
        :cell-renderer="treeCellRenderer"
        :get-node-height="() => props.nodeHeight"
        :nodes="filteredNodes"
        :use-time-slicing="true"
        :viewport-height="300"
      />
    </div>
  </div>
</template>

<style lang="scss">
.base-tree {
  .p-checkbox-checked > .p-checkbox-box {
    @apply bg-sky-300 border-sky-300 !important;
  }

  .tree-node {
    @apply w-full h-full flex flex-row flex-grow items-center justify-start;

    .tree-node-content {
      @apply rounded-md h-full px-4 flex items-center text-text-secondary;

      &:hover {
        @apply bg-gray-100;
      }

      &.highlight {
        @apply text-brand bg-blue-100/50;
      }

      &.tree-node-selectable {
        @apply cursor-pointer;
      }

      &.tree-node-disabled {
        @apply opacity-60;
      }

      .tree-toggler {
        @apply mr-2 w-8 h-8 shrink-0 flex items-center justify-center;
      }
    }
  }
}
</style>
